import React, { useState, memo } from 'react';
import { ChevronDown } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
const SERVICES_DATA = [{
  id: '01',
  title: 'Radar Normativo & Legislativo 360°',
  desc: 'Monitoreo inteligente y trazable de legislación, regulación y criterios de aplicación. Convertimos cambios y señales de agenda en prioridades ejecutivas.',
  entregables: ['Brief semanal (1 pág)', 'Alertas y semáforo por tema', 'Framework Now / Next / Later']
}, {
  id: '02',
  title: 'Matriz de Impacto y Riesgo Regulatorio',
  desc: 'Traducimos normas en decisiones: impacto operativo, financiero y reputacional, con escenarios y acciones recomendadas.',
  entregables: ['Matriz impacto × probabilidad × tiempo', 'Escenarios A/B/C', 'Plan de mitigación']
}, {
  id: '03',
  title: 'Comparativos Normativos (Version Tracking)',
  desc: 'Cuando cambia el texto, cambia el riesgo. Analizamos versiones, enmiendas y reglamentaciones con foco en consecuencias prácticas.',
  entregables: ['Comparativo de versiones', 'Red flags', 'Síntesis ejecutiva y hoja de acción']
}, {
  id: '04',
  title: 'Stakeholder Mapping & Poder Real',
  desc: 'Mapeamos decisores e influenciadores, posiciones e incentivos, para diseñar rutas de relación y acuerdos realistas.',
  entregables: ['Mapa poder/interés/posición', 'Red de vínculos', 'Agenda de reuniones y guiones']
}, {
  id: '05',
  title: 'Estrategia de Incidencia & Coaliciones',
  desc: 'Diseñamos estrategias de posicionamiento institucional basadas en evidencia, narrativa y articulación con aliados para construir voz, posición y resultados.',
  entregables: ['Advocacy plan', 'Argument bank', 'One-pagers', 'Hoja de ruta de relacionamiento']
}, {
  id: '06',
  title: 'Dossiers Técnicos para Decisores',
  desc: 'Documentos breves, sólidos y utilizables: lo que se lleva a una reunión y sostiene una posición institucional.',
  entregables: ['Position papers', 'Propuestas de redacción', 'Notas técnicas']
}, {
  id: '07',
  title: 'War Room Regulatorio (30–90 días)',
  desc: 'Operación intensiva para coyunturas críticas. Disciplina de seguimiento, decisiones rápidas y ejecución coordinada.',
  entregables: ['Sala de situación', 'Daily/weekly brief', 'Tablero de hitos y minutas', 'Tracking de cambios']
}, {
  id: '08',
  title: 'Implementación & Gestión Operativa del Cambio',
  desc: 'Bajamos la estrategia a procesos: contratos, políticas internas, capacitación y control.',
  entregables: ['Roadmap 30/60/90', 'Rediseño de políticas', 'Checklists, KPIs y gobernanza']
}, {
  id: '09',
  title: 'Litigiosidad & Contingencias',
  desc: 'Prevención y reducción de exposición, orden documental, lineamientos probatorios, gestión de riesgo y costo esperado.',
  entregables: ['Diagnóstico de contingencias', 'Medidas preventivas', 'Estándares internos', 'Coordinación externa']
}, {
  id: '10',
  title: 'Briefing Ejecutivo',
  desc: 'Sesiones de 60–90 minutos para ordenar decisiones: qué cambia, qué importa y qué se decide hoy.',
  entregables: ['Deck corto', 'Q&A estructurado', 'Decision memo', 'Próximos pasos']
}, {
  id: '11',
  title: 'Reporte Ejecutivo & M&E',
  desc: 'Medimos contribución, resultados y aprendizajes para sostener consistencia institucional y mejorar el ciclo de decisión.',
  entregables: ['KPIs', 'Reporte trimestral', 'Lecciones aprendidas', 'Ajuste de estrategia']
}];
function ServiceCard({
  service
}: {
  service: (typeof SERVICES_DATA)[0];
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  return <div className="bg-white border border-silver-main/30 rounded-lg p-6 md:p-8 hover:shadow-md transition-all duration-300 group relative overflow-hidden flex flex-col h-full" data-id="element-218">
      {/* Top Gradient Border on Hover */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-petrol-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-219"></div>

      <span className="font-body text-xs font-semibold text-silver-dark mb-3 block" data-id="element-220">
        {service.id}
      </span>

      <h3 className="font-body font-semibold text-lg md:text-xl text-petrol-dark mb-2" data-id="element-221">
        {service.title}
      </h3>

      <p className="font-body text-sm text-gray-600 leading-relaxed mb-5 flex-grow" data-id="element-222">
        {service.desc}
      </p>

      <div className="mt-auto pt-3 border-t border-silver-main/20" data-id="element-223">
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center justify-between w-full text-left font-body text-sm font-medium text-petrol-medium hover:text-petrol-dark transition-colors focus:outline-none" data-id="element-224">
          <span data-id="element-225">Ver entregables típicos</span>
          <ChevronDown size={16} className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-226" />
        </button>

        <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0 mt-0'}`} data-id="element-227">
          <div className="overflow-hidden" data-id="element-228">
            <ul className="space-y-2" data-id="element-229">
              {service.entregables.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-230">
                  <span className="text-silver-dark mr-2 mt-0.5 text-xs" data-id="element-231">
                    ■
                  </span>
                  <span className="font-body text-sm text-gray-500 leading-tight" data-id="element-232">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}
export function Servicios() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  return <section id="servicios" className="py-10 md:py-14 bg-[#F4F6F8]" data-id="element-233">
      <div className="max-w-7xl mx-auto px-6 md:px-8" data-id="element-234">
        <div ref={ref} className={`mb-8 md:mb-10 flex flex-col items-center text-center scroll-animate ${isVisible ? 'is-visible' : ''}`} data-id="element-235">
          <div className="w-16 h-px bg-silver-main mx-auto mb-6" data-id="element-236"></div>
          <span className="font-body text-xs uppercase tracking-widest text-silver-dark mb-5 block text-center" data-id="element-237">
            Servicios / Portfolio
          </span>
          <h2 className="font-heading text-4xl md:text-5xl text-petrol-dark leading-tight mb-5 max-w-3xl mx-auto text-center" data-id="element-238">
            Portafolio modular para anticipar, decidir y ejecutar
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed text-center" data-id="element-239">
            Nuestros servicios se activan como Retainer mensual, War Room (30–90
            días) o proyectos con entregables cerrados. Se pueden contratar de
            forma independiente o como un sistema integrado.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-id="element-240">
          {SERVICES_DATA.map(service => <ServiceCard key={service.id} service={service} data-id="element-241" />)}
        </div>
      </div>
    </section>;
}